import type { OrganizationGpuJobDto } from "@dutyon/apis";
import { type DutyBoundaryDiagnostics, diagnoseDutyBoundaries } from "@dutyon/apis/algorithm";

export type RemoteGpuBoundaryState =
  | { status: "unavailable"; reason: string }
  | {
      status: "checked";
      diagnostics: DutyBoundaryDiagnostics;
      details: {
        code: DutyBoundaryDiagnostics["issues"][number]["code"];
        dayString: string;
        nurseId: string;
      }[];
    };

/** Uses only authorized cloud results, never edited/base cells or current shift definitions. */
export function diagnoseRemoteGpuBoundaries(
  jobs: OrganizationGpuJobDto[],
): RemoteGpuBoundaryState | null {
  if (jobs.length < 2) return null;
  try {
    const first = jobs[0];
    const context = first.result?.boundaryContext;
    if (!context) throw new Error();
    const assignments: number[] = [];
    const boundaryDays: number[] = [];
    let dayCount = 0;
    for (const job of jobs) {
      if (
        job.status !== "SUCCEEDED" ||
        !job.result ||
        job.teamId !== first.teamId ||
        job.app !== first.app ||
        JSON.stringify(job.input.nurseIds) !== JSON.stringify(first.input.nurseIds) ||
        JSON.stringify(job.input.workShiftIds) !== JSON.stringify(first.input.workShiftIds) ||
        JSON.stringify(job.result.boundaryContext) !== JSON.stringify(context)
      )
        throw new Error();
      const start = Date.parse(`${job.input.startDayString}T00:00:00Z`);
      if (start !== Date.parse(`${first.input.startDayString}T00:00:00Z`) + dayCount * 86400000)
        throw new Error();
      if (dayCount) boundaryDays.push(dayCount);
      const dates = Array.from({ length: job.input.duration }, (_, day) =>
        new Date(start + day * 86400000).toISOString().slice(0, 10),
      );
      if (Object.keys(job.result.schedule).some((date) => !dates.includes(date))) throw new Error();
      for (const date of dates) {
        const cells = new Map<string, number>();
        for (const [shift, nurses] of Object.entries(job.result.schedule[date] ?? {})) {
          const index = first.input.workShiftIds.indexOf(shift);
          if (index < 0) throw new Error();
          for (const nurse of nurses) {
            if (!first.input.nurseIds.includes(nurse) || cells.has(nurse)) throw new Error();
            cells.set(nurse, index);
          }
        }
        for (const nurse of first.input.nurseIds) assignments.push(cells.get(nurse) ?? 0xffffffff);
      }
      dayCount += job.input.duration;
    }
    const diagnostics = diagnoseDutyBoundaries({
      nurseCount: first.input.nurseIds.length,
      dayCount,
      assignments,
      shiftFlags: context.shiftFlags,
      rules: context.rules,
      boundaryDays,
    });
    return {
      status: "checked",
      diagnostics,
      details: diagnostics.issues.map((issue) => ({
        code: issue.code,
        dayString: new Date(
          Date.parse(`${first.input.startDayString}T00:00:00Z`) + issue.day * 86400000,
        )
          .toISOString()
          .slice(0, 10),
        nurseId: first.input.nurseIds[issue.nurse],
      })),
    };
  } catch {
    return {
      status: "unavailable",
      reason:
        "기간별 생성 당시 규칙·근무 분류 또는 결과가 일치하지 않아 경계를 검사하지 못했습니다.",
    };
  }
}
export function remoteGpuBoundaryMessage(state: RemoteGpuBoundaryState | null) {
  if (!state) return "기간 경계 검사 결과가 없습니다.";
  if (state.status === "unavailable") return state.reason;
  const { totalIssueCount, missingAssignmentCount } = state.diagnostics;
  return `기간 경계의 추가 규칙 경고 ${totalIssueCount.toLocaleString("ko-KR")}건.${missingAssignmentCount ? ` 미배정 ${missingAssignmentCount.toLocaleString("ko-KR")}칸은 검사하지 못했습니다.` : ""} 이전 기간과의 연결 및 인원·고정 조건을 포함한 전체 근무표 검사는 별도로 확인해 주세요.`;
}

export const remoteGpuBoundaryLabels: Record<
  DutyBoundaryDiagnostics["issues"][number]["code"],
  string
> = {
  "consecutive-work": "연속 근무 초과",
  "consecutive-night": "연속 야간 초과",
  "forbidden-pattern": "금지 근무 패턴",
  "avoidance-pattern": "기피 근무 패턴",
  "pongdang-pattern": "퐁당퐁당 근무 패턴",
};
